import type { FC } from 'react';
import { Alert, Button, Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { AssetCreateFormFields } from './components/AssetCreateFormFields/AssetCreateFormFields';
import { useAssetCreateModal } from './hooks/useAssetCreateModal';

export interface AssetCreateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const AssetCreateModal: FC<AssetCreateModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation('assets');
  const {
    form,
    phase,
    isBusy,
    isPartialSuccess,
    assetFieldsDisabled,
    canAssignPlan,
    assignError,
    assetTypeOptions,
    assetModelOptions,
    siteOptions,
    zoneOptions,
    parentAssetOptions,
    landlordOptions,
    planOptions,
    selectedAssetTypeId,
    selectedSiteId,
    setSelectedAssetTypeId,
    setSelectedSiteId,
    handleFinish,
    handleRetryAssign,
    handleDoneWithoutPlan,
    handleModalCancel,
    customFields,
    customFieldsLoading,
  } = useAssetCreateModal({ visible, onCancel, onSuccess });

  return (
    <Modal
      title={t('create.title', 'Add New Asset')}
      open={visible}
      onCancel={handleModalCancel}
      onOk={() => form.submit()}
      confirmLoading={phase === 'creating'}
      okButtonProps={{ disabled: isPartialSuccess || phase === 'assigning' }}
      okText={t('create.submit', 'Create')}
      width={600}
      destroyOnHidden
      footer={
        isPartialSuccess
          ? [
              <Button key="done" onClick={handleDoneWithoutPlan}>
                {t('create.doneWithoutPlan', 'Done without plan')}
              </Button>,
              <Button
                key="retry"
                type="primary"
                loading={phase === 'assigning'}
                onClick={() => {
                  void handleRetryAssign();
                }}
              >
                {t('create.retryAssign', 'Retry assignment')}
              </Button>,
            ]
          : undefined
      }
    >
      {isPartialSuccess ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t(
            'create.partialSuccess',
            'Asset was created, but preventive plan assignment failed',
          )}
          description={assignError ?? undefined}
        />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          void handleFinish(values);
        }}
        initialValues={{ assetName: '', manufacturer: '' }}
        style={{ marginTop: 16 }}
      >
        <AssetCreateFormFields
          form={form}
          assetFieldsDisabled={assetFieldsDisabled}
          canAssignPlan={canAssignPlan}
          isPartialSuccess={isPartialSuccess}
          isBusy={isBusy}
          selectedAssetTypeId={selectedAssetTypeId}
          selectedSiteId={selectedSiteId}
          assetTypeOptions={assetTypeOptions}
          assetModelOptions={assetModelOptions}
          siteOptions={siteOptions}
          zoneOptions={zoneOptions}
          parentAssetOptions={parentAssetOptions}
          landlordOptions={landlordOptions}
          planOptions={planOptions}
          customFields={customFields}
          customFieldsLoading={customFieldsLoading}
          onAssetTypeChange={(assetTypeId) => {
            setSelectedAssetTypeId(assetTypeId);
            form.setFieldsValue({ customFields: {} });
          }}
          onSiteChange={setSelectedSiteId}
        />
      </Form>
    </Modal>
  );
};
